import React from "react";
import { useBusinessContext } from "@/contexts/business/BusinessContext";

interface WeeklyIssueProps {
  onOpenConnectionModal?: () => void;
}

/**
 * Retail Ranker — the Weekly Issue.
 *
 * A weekly briefing written for an indie shopkeeper. Every block is a
 * click-to-expand row: scannable in 60 seconds, one click deeper for
 * "what this means" + "what to do about it" + the evidence. No jargon,
 * no dashboards (except the one intentional Standing panel).
 */

const serif = { fontFamily: '"Playfair Display", Georgia, serif' } as const;

type SignalKind =
  | "brand" | "product" | "service" | "workshop"
  | "class" | "event" | "experience" | "seasonal" | "destination";

type ScoreState = "weak" | "mixed" | "strong";
type IndicatorKey = "visibility" | "trust" | "relevance" | "momentum";

type Signal = {
  kind: SignalKind;
  term: string;
  headline: string;      // short takeaway shown on the row
  meaning: string;       // "what this means for your store"
  doThis: string;        // "one thing to try"
  evidence: string[];    // 2–3 short data lines
};

type Score = {
  state: ScoreState;
  reading: string;       // plain-english one-liner shown on the tile
  meaning: string;       // longer explanation for the expand
  drivers: string[];     // 2–3 bullets: what's pushing it up or down
  moveIt: string;        // the one thing that would improve it
};

type OpportunityStep = { do: string; how: string };
type Opportunity = {
  indicator: IndicatorKey;
  title: string;
  why: string;
  effort: string;
  payoff: string;
  howSure: string;       // plain-english confidence
  steps: OpportunityStep[];
  evidence: string[];
};

type SecondaryItem = {
  indicator: IndicatorKey;
  headline: string;
  detail: string;
  meaning: string;
  doThis: string;
};

const WeeklyIssue: React.FC<WeeklyIssueProps> = ({ onOpenConnectionModal }) => {
  let currentBusinessName: string | undefined;
  try {
    const ctx = useBusinessContext() as any;
    currentBusinessName = ctx?.currentBusiness?.name;
  } catch {
    currentBusinessName = undefined;
  }
  const storeName = currentBusinessName || "Boujee Boutique";

  const issue: {
    number: number;
    weekOf: string;
    editorsRead: string;
    signals: Signal[];
    scores: Record<IndicatorKey, Score>;
    opportunity: Opportunity;
    improvement: SecondaryItem;
    watch: SecondaryItem;
  } = {
    number: 14,
    weekOf: "Week of March 10, 2026",
    editorsRead:
      "Two things stand out this week. Shoppers near you are asking for hands-on experiences — pottery classes, girls' night ideas — faster than they're asking for products. And one of the things you already do best (workshops) still isn't showing up when they search for it.",

    signals: [
      {
        kind: "workshop",
        term: "Beginner pottery classes",
        headline: "Up 41% this month near you",
        meaning:
          "More people in your area are looking for a hands-on class they can take with a friend or their kids. You already run pottery on Thursday nights, but nothing on your site or Google profile says so — which means these searches are almost all landing on other places.",
        doThis:
          "Add a short page titled 'Pottery Nights at Boujee Boutique' with the price, what's included, and the next three dates. That alone usually puts you in the running within 2–3 weeks.",
        evidence: [
          "148 local searches for 'pottery class near me' in the last 30 days (up from 105).",
          "No shop within 10 miles currently ranks in the top 5 for this term.",
          "Your own Instagram posts about pottery nights are your highest-engagement content this quarter.",
        ],
      },
      {
        kind: "service",
        term: "Same-day floral delivery",
        headline: "Steady demand, only two competitors visible",
        meaning:
          "People search this most on Fridays and around birthdays. It's a service you already offer through your neighbor florist arrangement, but shoppers don't know that from anything they can find online.",
        doThis:
          "Add one line to your homepage and Google profile: 'Same-day flower delivery available — order by 2pm.' Small change, real signal.",
        evidence: [
          "62 local searches per week, consistent for 3 months running.",
          "Only two shops in your area appear in the local pack.",
          "0 mentions of 'same-day' or 'delivery' currently on your site.",
        ],
      },
      {
        kind: "event",
        term: "First communion gifts",
        headline: "Peaks in 3 weeks — start now",
        meaning:
          "This is a seasonal spike. Searches for communion gifts jump sharply about 3 weeks before Easter and stay high through May. If you're not visible by early April, most of that demand is already gone.",
        doThis:
          "Photograph 6–8 things you'd recommend (crosses, jewelry, keepsake books) and post a short 'Gifts for First Communion' guide this week.",
        evidence: [
          "Search volume ramps 3× in the two weeks before Easter every year.",
          "You carry 12 items that fit this category — none are tagged this way online.",
          "Two competitors published guides last year and captured most of the local traffic.",
        ],
      },
      {
        kind: "experience",
        term: "Girls' night out activities",
        headline: "Brand new signal — nobody local is showing up yet",
        meaning:
          "This is a search that barely existed 6 months ago and is climbing fast in your area. It usually means groups of friends planning a Friday or Saturday night together. Nobody nearby has claimed this yet, which is unusual and worth moving on.",
        doThis:
          "If you host private events, mention them explicitly (e.g. 'private pottery + wine nights for groups of 6–10'). Even one page ranks quickly on a new term.",
        evidence: [
          "89 local searches last month — up from under 10 in September.",
          "No local business currently ranks in the top 10 for this term.",
          "Your existing private-event photos on Instagram get 2× the engagement of retail posts.",
        ],
      },
    ],

    scores: {
      visibility: {
        state: "mixed",
        reading: "People who know your name find you. People who don't, usually don't.",
        meaning:
          "Visibility is about whether you show up when someone is looking. When shoppers search for 'Boujee Boutique' by name, you're the first result — good. When they search things like 'gift shop near me' or 'pottery class near me,' you rarely appear in the first page of results.",
        drivers: [
          "Your Google Business profile is complete and active.",
          "Your site doesn't include the phrases shoppers actually type.",
          "Only 3 pages on your site are indexed by Google (typical stores have 20–40).",
        ],
        moveIt:
          "Adding the pottery workshop page (this week's opportunity) is the fastest single move — it targets a term that's actively climbing.",
      },
      trust: {
        state: "strong",
        reading: "You've earned this. Reviews are excellent and you reply fast.",
        meaning:
          "Trust is what makes someone actually walk in or click 'buy' once they find you. Yours is a real asset — 4.8 stars across 214 reviews, and your reply time to new reviews is faster than 92% of retailers within a 10-mile radius.",
        drivers: [
          "214 reviews averaging 4.8 stars.",
          "Reply time on new reviews is under 24 hours.",
          "Photo count on your Google profile is above average.",
        ],
        moveIt:
          "Keep the reply cadence up. If you want to compound it, ask one satisfied workshop attendee per week to leave a review — that keeps the flow fresh.",
      },
      relevance: {
        state: "weak",
        reading: "Shoppers are searching for what you actually sell — using words your site never uses.",
        meaning:
          "Relevance is the gap between what your neighborhood is looking for and how you describe your store online. Right now, your site talks about your brand and story (which matters), but not about the specific things people type into Google. That's why traffic is flat even though your product mix is strong.",
        drivers: [
          "3 of your top 4 sellers are never named on your site.",
          "Workshops are mentioned only on your About page.",
          "'Gift shop,' 'boutique,' and specific brand names appear 0 times on your homepage.",
        ],
        moveIt:
          "Name the things you sell and teach in plain language on your homepage — brands, workshop types, services. This is usually the fastest score to move.",
      },
      momentum: {
        state: "mixed",
        reading: "You're holding steady, but nothing new is picking up.",
        meaning:
          "Momentum tracks whether your recent work is bringing in new visibility. You've stayed level over the last 90 days — you haven't lost ground, but nothing you've published recently is climbing either. A single new page or post that hits a live search term usually restarts the cycle.",
        drivers: [
          "No new site pages published in the last 30 days.",
          "3 Google profile posts, all announcements (not searchable topics).",
          "Instagram is active, but Instagram doesn't feed Google search.",
        ],
        moveIt:
          "The pottery workshop page would double as a momentum move — new content, on a rising term.",
      },
    },

    opportunity: {
      indicator: "relevance",
      title: "Add a pottery workshop page before demand peaks.",
      why:
        "Searches for beginner pottery classes near you are up 41% this month, and no shop within 10 miles is ranking for that term yet. You already host these on Thursdays — a single page naming the workshop, the price, and the next three dates would put you in front of that demand within 2–3 weeks. This is the smallest possible move for the biggest possible signal.",
      effort: "About 45 minutes",
      payoff: "Likely to bring in new bookings within a month",
      howSure: "Very sure — the demand is already there and the competition is thin",
      steps: [
        {
          do: "Write a short page (250–400 words)",
          how: "Title it 'Pottery Nights at Boujee Boutique.' Cover: what you'll make, who it's for, what's included (wine? snacks?), price, and the next 3 Thursday dates.",
        },
        {
          do: "Add 3 real photos",
          how: "Use photos from a recent Thursday night — hands at the wheel, finished pieces, the room. Real beats stock every time.",
        },
        {
          do: "Link it from your homepage and Google profile",
          how: "One 'Book a workshop' button on your homepage. On your Google Business profile, add it as a 'Service.'",
        },
        {
          do: "Post it once to Instagram",
          how: "Not to drive traffic — to signal freshness. Google notices when a URL gets its first inbound activity.",
        },
      ],
      evidence: [
        "Local searches for 'pottery class near me': 148 in the last 30 days, up from 105.",
        "Top-ranking result within 10 miles: a community college, not a retailer.",
        "Your Instagram pottery posts have 2.3× the engagement of your product posts.",
        "Similar pages in comparable retail stores typically reach the top 5 within 3 weeks.",
      ],
    },

    improvement: {
      indicator: "trust",
      headline: "Your review reply time dropped from 4 days to under 1.",
      detail:
        "You've replied to every new review within 24 hours for three weeks running.",
      meaning:
        "This matters more than most owners realize. Shoppers reading your reviews notice when the owner shows up — it's often the deciding factor between two similar stores. Google also notices, and rewards active profiles in the local map results.",
      doThis:
        "Keep going. If you want to compound it, add a one-sentence reply even to 5-star reviews with no comment — it looks like you care, because you do.",
    },

    watch: {
      indicator: "momentum",
      headline: "Your site still leads with winter product — Easter is 3 weeks out.",
      detail:
        "Your homepage hero, top 3 Google profile photos, and pinned Instagram posts are all winter-themed. It's not hurting you this week, but it will next.",
      meaning:
        "Local shoppers start planning Easter and spring gifting about 3 weeks out. If your storefront (online) still looks like February when they're thinking about April, they'll assume you don't have what they need and keep scrolling.",
      doThis:
        "Swap the homepage hero image and top Google photo to something spring-forward this week. It doesn't have to be perfect — just current.",
    },
  };

  return (
    <div className="min-h-screen w-full bg-background py-10 lg:py-14 px-4 sm:px-8">
      <div
        className="mx-auto w-full max-w-4xl"
        style={{ fontFamily: "Inter, system-ui, sans-serif" }}
      >
        {/* ── Masthead ────────────────────────────────────────────── */}
        <header className="pb-6 border-b border-[hsl(var(--ink-deep))]">
          <div className="flex items-center justify-between gap-4 text-[10px] uppercase tracking-[0.28em] font-semibold text-[hsl(var(--ink-muted))]">
            <span>Retail Ranker</span>
            <span className="text-[hsl(var(--ink-faint))]">
              Issue No. {String(issue.number).padStart(2, "0")}
            </span>
          </div>
          <h1
            className="mt-6 text-4xl sm:text-5xl lg:text-[64px] font-normal text-[hsl(var(--ink-deep))] leading-[0.98] tracking-tight"
            style={serif}
          >
            The Weekly Issue
          </h1>
          <div className="mt-5 flex items-center gap-4 text-[10px] uppercase tracking-[0.28em] font-semibold text-[hsl(var(--ink-muted))]">
            <span>{issue.weekOf}</span>
            <span className="h-px flex-1 bg-[hsl(var(--rule))]" />
            <span className="text-[hsl(var(--ink-faint))]">{storeName}</span>
          </div>
        </header>

        {/* ── Editor's read ───────────────────────────────────────── */}
        <section className="mt-12">
          <SectionLabel index="01" label="What we noticed this week" />
          <p
            className="mt-5 text-2xl sm:text-3xl leading-[1.35] text-[hsl(var(--ink-deep))] tracking-tight"
            style={serif}
          >
            {issue.editorsRead}
          </p>
        </section>

        {/* ── Neighborhood signals ────────────────────────────────── */}
        <section className="mt-16 pt-10 border-t border-[hsl(var(--rule))]">
          <SectionLabel index="02" label="What shoppers near you are looking for" />
          <p className="mt-5 text-[15px] text-[hsl(var(--ink-muted))] leading-relaxed max-w-[58ch]">
            The strongest new search patterns in your neighborhood over the
            last 30 days — across products, brands, services, workshops,
            events, and experiences. Click any row for what it means for
            your store and the one small thing to try.
          </p>
          <ul className="mt-8 border-y border-[hsl(var(--rule))] divide-y divide-[hsl(var(--rule))]">
            {issue.signals.map((s) => (
              <SignalRow key={s.term} signal={s} />
            ))}
          </ul>
        </section>

        {/* ── The one opportunity ─────────────────────────────────── */}
        <section className="mt-16 pt-10 border-t border-[hsl(var(--rule))]">
          <SectionLabel index="03" label="The one move worth making this week" accent="brass" />
          <h2
            className="mt-5 text-3xl sm:text-4xl lg:text-[44px] leading-[1.1] text-[hsl(var(--ink-deep))] tracking-tight max-w-[24ch]"
            style={serif}
          >
            {issue.opportunity.title}
          </h2>
          <p className="mt-6 text-[17px] text-[hsl(var(--ink))] leading-[1.75] max-w-[62ch]">
            {issue.opportunity.why}
          </p>

          <dl className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-4 border-y border-[hsl(var(--rule))] py-5">
            <MetaPair label="Effort" value={issue.opportunity.effort} />
            <MetaPair label="Likely payoff" value={issue.opportunity.payoff} />
            <MetaPair label="How sure we are" value={issue.opportunity.howSure} />
          </dl>

          <div className="mt-8 flex flex-wrap items-center gap-5">
            <button
              onClick={onOpenConnectionModal}
              className="px-6 py-3 bg-[hsl(var(--gold))] text-[hsl(var(--gold-ink))] text-[11px] uppercase tracking-[0.25em] font-semibold hover:brightness-95 transition-all shadow-[0_10px_30px_-12px_hsl(var(--gold)/0.6)]"
            >
              Walk me through it
            </button>
          </div>

          {/* Expand — steps + evidence */}
          <div className="mt-8 space-y-3">
            <ExpandRow label="Step-by-step: how to do this in one sitting">
              <ol className="space-y-5">
                {issue.opportunity.steps.map((s, i) => (
                  <li key={s.do} className="flex gap-4">
                    <span
                      className="text-[hsl(var(--gold))] text-[11px] tracking-[0.2em] font-semibold pt-1"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <div className="text-[16px] text-[hsl(var(--ink-deep))] font-medium">
                        {s.do}
                      </div>
                      <p className="mt-1 text-[14px] text-[hsl(var(--ink-muted))] leading-relaxed max-w-[58ch]">
                        {s.how}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </ExpandRow>
            <ExpandRow label="Why we're recommending this: the evidence">
              <ul className="space-y-2 list-disc pl-5 text-[14px] text-[hsl(var(--ink))] leading-relaxed max-w-[62ch]">
                {issue.opportunity.evidence.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </ExpandRow>
          </div>
        </section>

        {/* ── The Standing panel (dashboard moment) ───────────────── */}
        <section className="mt-16 bg-[hsl(var(--plum))] text-[hsl(var(--background))] shadow-[0_40px_80px_-40px_hsl(var(--plum)/0.6)]">
          <div className="px-8 sm:px-12 pt-10 pb-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="h-px w-8 bg-[hsl(var(--gold))]" />
                <span className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[hsl(var(--gold))]">
                  Your store's standing
                </span>
              </div>
              <span className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--background)/0.6)]">
                Updated {issue.weekOf.toLowerCase()}
              </span>
            </div>
            <h2
              className="mt-6 text-3xl sm:text-4xl lg:text-[44px] leading-[1.1] tracking-tight max-w-[26ch]"
              style={serif}
            >
              The four things worth watching. Click any one to dig in.
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-[hsl(var(--background)/0.8)] max-w-[58ch]">
              We only track four numbers. Together they tell you why shoppers
              are (or aren't) finding you, and what would change that.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px border-t border-[hsl(var(--background)/0.12)] bg-[hsl(var(--background)/0.12)]">
            <ScoreTile name="Visibility" plain="Can shoppers find you?"     score={issue.scores.visibility} />
            <ScoreTile name="Trust"      plain="Do they believe you?"       score={issue.scores.trust} />
            <ScoreTile name="Relevance"  plain="Do you speak their words?"  score={issue.scores.relevance} signature />
            <ScoreTile name="Momentum"   plain="Are you gaining ground?"    score={issue.scores.momentum} />
          </div>
        </section>

        {/* ── What's improved ─────────────────────────────────────── */}
        <section className="mt-16 pt-10 border-t border-[hsl(var(--rule))]">
          <SectionLabel index="04" label="What got better since last week" />
          <h3
            className="mt-5 text-2xl sm:text-[28px] leading-[1.2] text-[hsl(var(--ink-deep))] tracking-tight max-w-[32ch]"
            style={serif}
          >
            {issue.improvement.headline}
          </h3>
          <p className="mt-4 text-[15px] text-[hsl(var(--ink-muted))] leading-relaxed max-w-[58ch]">
            {issue.improvement.detail}
          </p>
          <div className="mt-6 space-y-3">
            <ExpandRow label="Why this matters for your store">
              <p className="text-[15px] text-[hsl(var(--ink))] leading-relaxed max-w-[62ch]">
                {issue.improvement.meaning}
              </p>
            </ExpandRow>
            <ExpandRow label="How to keep the streak going">
              <p className="text-[15px] text-[hsl(var(--ink))] leading-relaxed max-w-[62ch]">
                {issue.improvement.doThis}
              </p>
            </ExpandRow>
          </div>
        </section>

        {/* ── Watch ───────────────────────────────────────────────── */}
        <section className="mt-16 pt-10 border-t border-[hsl(var(--rule))]">
          <SectionLabel index="05" label="Worth keeping an eye on" />
          <h3
            className="mt-5 text-2xl sm:text-[28px] leading-[1.2] text-[hsl(var(--ink-deep))] tracking-tight max-w-[32ch]"
            style={serif}
          >
            {issue.watch.headline}
          </h3>
          <p className="mt-4 text-[15px] text-[hsl(var(--ink-muted))] leading-relaxed max-w-[58ch]">
            {issue.watch.detail}
          </p>
          <div className="mt-6 space-y-3">
            <ExpandRow label="Why this matters for your store">
              <p className="text-[15px] text-[hsl(var(--ink))] leading-relaxed max-w-[62ch]">
                {issue.watch.meaning}
              </p>
            </ExpandRow>
            <ExpandRow label="The small thing to try this week">
              <p className="text-[15px] text-[hsl(var(--ink))] leading-relaxed max-w-[62ch]">
                {issue.watch.doThis}
              </p>
            </ExpandRow>
          </div>
        </section>

        {/* ── Colophon ────────────────────────────────────────────── */}
        <footer className="mt-20 pt-8 border-t border-[hsl(var(--ink-deep))] flex flex-wrap justify-between items-center gap-4 text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--ink-faint))] font-semibold">
          <span>Retail Ranker · Discoverability Intelligence</span>
          <span>Next issue Monday</span>
          <button className="hover:text-[hsl(var(--ink))] transition-colors">
            Browse past issues →
          </button>
        </footer>
      </div>
    </div>
  );
};

/* ── Building blocks ───────────────────────────────────────────── */

const SectionLabel: React.FC<{
  index: string;
  label: string;
  accent?: "brass" | "ink";
}> = ({ index, label, accent = "ink" }) => {
  const rule = accent === "brass" ? "bg-[hsl(var(--plum))]" : "bg-[hsl(var(--ink))]";
  const text = accent === "brass" ? "text-[hsl(var(--plum))]" : "text-[hsl(var(--ink))]";
  return (
    <div className="flex items-center gap-3">
      <span
        className="text-[hsl(var(--ink-faint))] text-[11px] tracking-[0.28em] font-semibold"
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        {index}
      </span>
      <span className={`h-px w-8 ${rule}`} />
      <span className={`text-[10px] uppercase tracking-[0.28em] font-semibold ${text}`}>
        {label}
      </span>
    </div>
  );
};

const MetaPair: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <dt className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[hsl(var(--ink-faint))]">
      {label}
    </dt>
    <dd className="mt-1 text-[15px] text-[hsl(var(--ink-deep))] leading-snug">
      {value}
    </dd>
  </div>
);

const ExpandRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <details className="group border border-[hsl(var(--rule))] bg-[hsl(var(--paper))] open:shadow-[0_20px_50px_-30px_rgba(45,58,48,0.18)] transition-shadow">
    <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-4 text-[12px] uppercase tracking-[0.22em] font-semibold text-[hsl(var(--ink))] hover:text-[hsl(var(--plum))] transition-colors">
      <span>{label}</span>
      <span
        aria-hidden
        className="text-[hsl(var(--gold))] transition-transform group-open:rotate-45 text-lg leading-none"
      >
        +
      </span>
    </summary>
    <div className="px-5 pb-6 pt-1">{children}</div>
  </details>
);

const SignalRow: React.FC<{ signal: Signal }> = ({ signal }) => (
  <li>
    <details className="group">
      <summary className="cursor-pointer list-none flex items-baseline gap-6 py-4 hover:bg-[hsl(var(--paper))] transition-colors">
        <span className="w-24 shrink-0 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-faint))] font-semibold">
          {signal.kind}
        </span>
        <span
          className="flex-1 text-xl sm:text-2xl text-[hsl(var(--ink-deep))] leading-snug"
          style={serif}
        >
          {signal.term}
        </span>
        <span className="hidden sm:inline text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--plum))] font-semibold">
          {signal.headline}
        </span>
        <span
          aria-hidden
          className="text-[hsl(var(--gold))] transition-transform group-open:rotate-45 text-lg leading-none pl-2"
        >
          +
        </span>
      </summary>
      <div className="pb-6 pl-[calc(6rem)] pr-2 grid gap-6 sm:grid-cols-3 sm:gap-8">
        <MiniBlock label="What this means for you">
          {signal.meaning}
        </MiniBlock>
        <MiniBlock label="One thing to try">{signal.doThis}</MiniBlock>
        <MiniBlock label="The evidence">
          <ul className="space-y-1.5 list-disc pl-4">
            {signal.evidence.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </MiniBlock>
      </div>
    </details>
  </li>
);

const MiniBlock: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[hsl(var(--plum))] mb-2">
      {label}
    </div>
    <div className="text-[14px] leading-relaxed text-[hsl(var(--ink))]">
      {children}
    </div>
  </div>
);

/**
 * The one intentional dashboard element in the issue — deep plum tile,
 * gold accents, expandable body for the plain-English explanation.
 */
const ScoreTile: React.FC<{
  name: string;
  plain: string;
  score: Score;
  signature?: boolean;
}> = ({ name, plain, score, signature }) => {
  const label =
    score.state === "strong" ? "Strong" : score.state === "mixed" ? "Mixed" : "Needs work";
  const filled = score.state === "strong" ? 3 : score.state === "mixed" ? 2 : 1;
  const isGood = score.state === "strong";
  return (
    <details
      className={`group relative bg-[hsl(var(--plum))] ${
        signature ? "ring-1 ring-inset ring-[hsl(var(--gold))]" : ""
      }`}
    >
      <summary className="cursor-pointer list-none p-8 lg:p-10">
        {signature && (
          <span className="absolute top-4 right-4 text-[9px] uppercase tracking-[0.28em] font-semibold text-[hsl(var(--gold))]">
            Signature
          </span>
        )}
        <div className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--gold))]" />
          <span className="text-[10px] uppercase tracking-[0.32em] font-semibold text-[hsl(var(--background)/0.75)]">
            {name}
          </span>
        </div>
        <div className="mt-1 text-[12px] text-[hsl(var(--background)/0.55)] italic">
          {plain}
        </div>

        <div
          className={`mt-5 text-5xl sm:text-6xl leading-none font-normal ${
            isGood ? "text-[hsl(var(--gold))]" : "text-[hsl(var(--background))]"
          }`}
          style={serif}
        >
          {label}
        </div>

        <div className="mt-6 flex items-center gap-1.5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className={`h-1.5 flex-1 ${
                i < filled ? "bg-[hsl(var(--gold))]" : "bg-[hsl(var(--background)/0.18)]"
              }`}
            />
          ))}
        </div>

        <p className="mt-6 text-[14px] leading-relaxed text-[hsl(var(--background)/0.85)] max-w-[38ch]">
          {score.reading}
        </p>

        <div className="mt-6 flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold text-[hsl(var(--gold))] group-open:opacity-0 transition-opacity">
          <span>Read the details</span>
          <span aria-hidden>+</span>
        </div>
      </summary>

      <div className="px-8 lg:px-10 pb-10 -mt-2 space-y-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[hsl(var(--gold))] mb-2">
            What this really means
          </div>
          <p className="text-[14px] leading-relaxed text-[hsl(var(--background)/0.9)]">
            {score.meaning}
          </p>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[hsl(var(--gold))] mb-2">
            What's driving the number
          </div>
          <ul className="space-y-1.5 list-disc pl-5 text-[14px] leading-relaxed text-[hsl(var(--background)/0.9)]">
            {score.drivers.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[hsl(var(--gold))] mb-2">
            The one thing that would move it
          </div>
          <p className="text-[14px] leading-relaxed text-[hsl(var(--background)/0.9)]">
            {score.moveIt}
          </p>
        </div>
      </div>
    </details>
  );
};

export default WeeklyIssue;